/**
 * The site page's chrome: "← Your sites", the host with its connection chip,
 * and the two tabs. Split from the page itself so the review sandbox can mount
 * the very same header instead of a look-alike copy of it.
 */

import type { ReactNode } from 'react'

import type { IWebsite } from '@glocal/schemas'

import { Chip } from '../../components/chip'

import { hostOf } from './languages'

export type SiteTab = 'languages' | 'settings'

export const SITE_TABS: readonly SiteTab[] = ['languages', 'settings']

export function SiteShell({
	website,
	connected,
	tab,
	onTab,
	onBack,
	children,
}: {
	website: IWebsite
	/** Is the domain pointed at us — a DNS-only question, never the switches. */
	connected: boolean
	tab: SiteTab
	onTab: (t: SiteTab) => void
	onBack: () => void
	children: ReactNode
}) {
	return (
		<div className="mx-auto flex max-w-[800px] flex-col gap-5">
			{/* Going back is a move up the hierarchy, so it reads before the thing
			    you'd be leaving — top left, where the eye starts. */}
			<div className="flex flex-col gap-1.5">
				<button
					onClick={onBack}
					className="self-start text-[12px] text-ink-soft transition-colors hover:text-ink"
				>
					← Your sites
				</button>
				<div className="flex min-w-0 items-center gap-2.5">
					<h1 className="truncate font-mono text-[20px] font-medium text-ink">{hostOf(website)}</h1>
					{connected ? <Chip tone="live">● Live</Chip> : <Chip tone="preview">Setting up</Chip>}
				</div>
			</div>

			<div className="flex gap-5 border-b border-line">
				{SITE_TABS.map((t) => (
					<button
						key={t}
						onClick={() => onTab(t)}
						className={
							'-mb-px border-b-2 py-2.5 text-[13px] font-medium capitalize transition-colors ' +
							(tab === t ? 'border-marigold text-ink' : 'border-transparent text-ink-mute hover:text-ink')
						}
					>
						{t}
					</button>
				))}
			</div>

			{children}
		</div>
	)
}
